import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TeamService } from '@/data/services';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import type { TeamPlayerAccess } from '@/types';

export type PlayerAccessArea = 'stats' | 'video';

export interface PlayerAccess {
  stats: boolean;
  video: boolean;
  isLoading: boolean;
  /** Teams whose coach closed stats: their scouting stays out of the Video screen. */
  statsClosedTeamIds: ReadonlySet<string>;
  /** Teams whose coach closed video: their matches stay out of the Video screen. */
  videoClosedTeamIds: ReadonlySet<string>;
  /** False for a player who belongs to no team yet (club-only or demo account). */
  hasOwnTeam: boolean;
  /** Signed in with the PLAYER role. */
  isPlayer: boolean;
  /** Team id → roster entry the coach linked to this account (VOL-341); empty for staff. */
  linkedPlayerIds: ReadonlyMap<string, string>;
  /** Teams the player belongs to (not only through the club): the only ones a coach can link in. */
  memberTeamIds: ReadonlySet<string>;
}

const NONE: ReadonlySet<string> = new Set();
const NO_LINK: ReadonlyMap<string, string> = new Map();
const OPEN: PlayerAccess = {
  stats: true,
  video: true,
  isLoading: false,
  statsClosedTeamIds: NONE,
  videoClosedTeamIds: NONE,
  hasOwnTeam: true,
  isPlayer: false,
  linkedPlayerIds: NO_LINK,
  memberTeamIds: NONE,
};

export interface LinkedPlayerView {
  /** "My page" is imposed: the linked roster entry of the selection, no player picker. */
  linked: boolean;
  /** A player account the coach could link in this selection but has not: say who can fix it. */
  showLinkHint: boolean;
}

/**
 * Whether the Stats selection opens the linked player's own page (VOL-341). Scouting shows
 * opponents, never the player's own roster: nothing is imposed there, and no hint either.
 */
export function resolveLinkedPlayerView(
  access: Pick<PlayerAccess, 'isPlayer' | 'linkedPlayerIds' | 'memberTeamIds'>,
  selection: { kind: 'OWN' | 'SCOUTING'; teamId: string },
): LinkedPlayerView {
  if (!access.isPlayer || selection.kind !== 'OWN') return { linked: false, showLinkHint: false };
  const all = selection.teamId === 'all';
  const linked = all ? access.linkedPlayerIds.size > 0 : access.linkedPlayerIds.has(selection.teamId);
  const member = all ? access.memberTeamIds.size > 0 : access.memberTeamIds.has(selection.teamId);
  return { linked, showLinkHint: !linked && member };
}

/**
 * Roster entry shown as "my page" for the Stats selection (VOL-341). One team selected: its own
 * link only, a link in another team is another roster entry. All teams: the linked entry with
 * most actions in the rows, so a player of U18 and Senior lands on the team they played for.
 */
export function resolveLinkedPlayerId(
  linkedPlayerIds: ReadonlyMap<string, string>,
  selectedTeamId: string,
  rows: readonly { playerId?: string }[],
): string | null {
  if (selectedTeamId !== 'all') return linkedPlayerIds.get(selectedTeamId) ?? null;
  const linked = new Set(linkedPlayerIds.values());
  if (linked.size <= 1) return linked.values().next().value ?? null;
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.playerId && linked.has(row.playerId)) counts.set(row.playerId, (counts.get(row.playerId) ?? 0) + 1);
  }
  const best = [...counts].sort((a, b) => b[1] - a[1])[0];
  return best?.[0] ?? linked.values().next().value ?? null;
}

/**
 * Match-level decision from the match's own team (VOL-187): a player of U18 and
 * Senior may open the stats of one team and not the other.
 */
export function canOpenMatchArea(
  access: Pick<PlayerAccess, 'stats' | 'video' | 'isLoading' | 'statsClosedTeamIds' | 'videoClosedTeamIds'>,
  match: { teamId?: string | null; team?: { id: string } | null },
  area: PlayerAccessArea,
): boolean {
  if (access.isLoading || !access[area]) return false;
  const closed = area === 'stats' ? access.statsClosedTeamIds : access.videoClosedTeamIds;
  // Same fallback as useStats and VideoScreen: some payloads only carry the nested team.
  const teamId = match.teamId ?? match.team?.id;
  return !teamId || !closed.has(teamId);
}

/**
 * Menu-level access follows the teams the player belongs to. A club player also sees the
 * other teams of the club: their settings must not reopen a screen the player's own coach
 * closed. Without any membership (club-only or demo account), all visible teams count.
 * The backend still decides match by match.
 */
export function resolvePlayerAccess(rows: readonly TeamPlayerAccess[]): Pick<PlayerAccess, 'stats' | 'video'> {
  const own = rows.filter((row) => row.isMember);
  const relevant = own.length > 0 ? own : rows;
  if (relevant.length === 0) return { stats: true, video: true };
  return {
    stats: relevant.some((row) => row.playerStatsAccess),
    video: relevant.some((row) => row.playerVideoAccess),
  };
}

/** What the signed-in user may open; staff roles are never restricted. */
export function usePlayerAccess(): PlayerAccess {
  const isPlayer = useAuthController((s) => s.user?.role === 'PLAYER');
  // Under the 'teams' prefix: a coach saving the settings invalidates it with the team lists.
  const { data, isLoading } = useQuery({
    queryKey: ['teams', 'player-access'],
    queryFn: () => TeamService.getPlayerAccess(),
    staleTime: 60_000,
    enabled: isPlayer,
  });

  return useMemo(() => {
    if (!isPlayer) return OPEN;
    const rows = data ?? [];
    return {
      ...resolvePlayerAccess(rows),
      isLoading,
      statsClosedTeamIds: new Set(rows.filter((r) => !r.playerStatsAccess).map((r) => r.teamId)),
      videoClosedTeamIds: new Set(rows.filter((r) => !r.playerVideoAccess).map((r) => r.teamId)),
      hasOwnTeam: isLoading || rows.some((r) => r.isMember),
      isPlayer: true,
      linkedPlayerIds: new Map(
        rows.flatMap((r) => (r.isMember && r.linkedPlayerId ? [[r.teamId, r.linkedPlayerId] as const] : [])),
      ),
      memberTeamIds: new Set(rows.filter((r) => r.isMember).map((r) => r.teamId)),
    };
  }, [isPlayer, data, isLoading]);
}
